import { EFarmDisplayOption, ESiteDisplayOption } from 'FeatureModules/Auth/types/company.types';
import { ESiteType, ELandOwnership, IFarmSite, ISite } from 'FeatureModules/Site/types/site.types';

export const SiteTypeLabels: Record<ESiteType, string> = {
  [ESiteType.FARM]: 'Farm',
  [ESiteType.MINE]: 'Mine',
  [ESiteType.FOREST]: 'Forest',
  [ESiteType.COLLECTION_SITE]: 'Collection site',
  [ESiteType.ORIGIN_SITE]: 'Origin site',
  [ESiteType.PLANT]: 'Plant',
  [ESiteType.PROCESSING_SITE]: 'Processing site',
  [ESiteType.FACTORY]: 'Factory',
  [ESiteType.MANUFACTURING_SITE]: 'Manufacturing site',
  [ESiteType.WAREHOUSE]: 'Warehouse',
  [ESiteType.HANDLING_SITE]: 'Handling site',
  [ESiteType.GROCERY_STORE]: 'Grocery store',
  [ESiteType.DISTRIBUTION_SITE]: 'Distribution site',
  [ESiteType.GENERIC_SITE]: 'Generic site',
  [ESiteType.CLUSTER]: 'Cluster',
};

export const LandOwnershipLabels: Record<string, string> = {
  [ELandOwnership.PRIVATE_OWNERSHIP]: 'Private ownership',
  [ELandOwnership.PUBLIC_OWNERSHIP]: 'Public ownership',
  [ELandOwnership.COMMUNAL_OWNERSHIP]: 'Communal ownership',
  [ELandOwnership.STATE_OWNERSHIP]: 'State ownership',
  [ELandOwnership.TENANCY]: 'Tenancy',
  [ELandOwnership.CONCESSION]: 'Concession',
  [ELandOwnership.EASEMENT]: 'Easement',
  [ELandOwnership.COLLECTIVE_FARMS]: 'Collective farms',
  [ELandOwnership.TRUSTS]: 'Trusts',
  [ELandOwnership.COMMON_PROPERTY_REGIMES]: 'Common property regimes',
  [ELandOwnership.ADVERSE_POSSESSION]: 'Adverse possession',
  [ELandOwnership.LAND_COOPERATIVES]: 'Land cooperatives',
  [ELandOwnership.USUFRUCT_RIGHTS]: 'Usufruct rights',
  [ELandOwnership.OTHER]: 'Other',
};

export const LandOwnershipDescriptions: Record<string, string> = {
  [ELandOwnership.PRIVATE_OWNERSHIP]:
    'Land owned by individuals or entities (such as corporations) with full control and rights to use, sell, or transfer the property.',
  [ELandOwnership.PUBLIC_OWNERSHIP]:
    'Land owned by the government or public authorities, typically used for public purposes like parks, roads, or government buildings.',
  [ELandOwnership.COMMUNAL_OWNERSHIP]:
    'Land owned collectively by a community or group, where all members have rights to use and manage the land collectively.',
  [ELandOwnership.STATE_OWNERSHIP]:
    'Land owned by the state or government, often used for public or national interests, and not intended for private ownership.',
  [ELandOwnership.TENANCY]:
    'A legal arrangement where the tenant has the right to use the land owned by another party (landlord) for a specified period in exchange for rent or services.',
  [ELandOwnership.CONCESSION]:
    'A grant of land rights by a government or authority to a private party for a specific purpose, such as mining or agriculture, usually for a limited period.',
  [ELandOwnership.EASEMENT]:
    'A right to use part of the land owned by another party for a specific purpose, such as access to a road or utilities, without owning the land.',
  [ELandOwnership.COLLECTIVE_FARMS]:
    'A type of communal ownership where land is collectively owned and managed by a group, often in the context of agricultural production.',
  [ELandOwnership.TRUSTS]:
    'Land held by one party (the trustee) for the benefit of another (the beneficiary), often used for managing land and property for future generations.',
  [ELandOwnership.COMMON_PROPERTY_REGIMES]:
    'Land or resources owned and managed by a community or group, where access and use are regulated by collective rules.',
  [ELandOwnership.ADVERSE_POSSESSION]:
    ' legal principle allowing a person to claim ownership of land if they have occupied it for a certain period, under specific conditions, without the permission of the original owner.',
  [ELandOwnership.LAND_COOPERATIVES]:
    'A form of ownership where a group of individuals collectively own and manage land, often for agricultural or housing purposes.',
  [ELandOwnership.USUFRUCT_RIGHTS]:
    'A temporary right to use and benefit from land allocated by another party (often the state), which can be inherited but does not grant full ownership.',
  [ELandOwnership.OTHER]:
    'Any land ownership arrangement that does not fit into the defined categories, including unique or hybrid forms of ownership or rights.',
};

interface IDisplayOptionParams {
  /** The farm site to extract display value from */
  site: {
    externalId?: ISite['externalId'];
    title?: ISite['title'];
    farmOwner?: IFarmSite['farmOwner'];
    siteType?: ISite['siteType'];
  };
  /** The display option configured by the company for farms */
  farmDisplayOption: EFarmDisplayOption;
  /** The display option configured by the company for sites */
  siteDisplayOption: ESiteDisplayOption;
}

export const getSiteTitle = ({
  site,
  farmDisplayOption,
  siteDisplayOption,
}: IDisplayOptionParams): string => {
  if (site.siteType === ESiteType.FARM) {
    switch (farmDisplayOption) {
      case EFarmDisplayOption.FARM_ID:
        return site.externalId || 'No ID';
      case EFarmDisplayOption.FARM_NAME:
        return site.title || 'Unnamed farm';
      case EFarmDisplayOption.FARM_OWNER_NAME:
        return site.farmOwner || 'No farm owner name';
      default:
        return site.title || 'Unnamed farm';
    }
  }
  if (site.siteType !== ESiteType.CLUSTER) {
    switch (siteDisplayOption) {
      case ESiteDisplayOption.SITE_ID:
        return site.externalId || 'No ID';
      case ESiteDisplayOption.SITE_NAME:
        return site.title || 'Unnamed site';
      default:
        return site.title || 'Unnamed site';
    }
  }

  if (site.siteType === ESiteType.CLUSTER) {
    return site.title || 'Unnamed cluster';
  }

  return site.title || 'Unnamed site';
};

export const getSiteSecondTitle = ({
  site,
  farmDisplayOption,
  siteDisplayOption,
}: IDisplayOptionParams): string | undefined => {
  if (site.siteType === ESiteType.FARM) {
    switch (farmDisplayOption) {
      case EFarmDisplayOption.FARM_ID:
        return site.title;
      case EFarmDisplayOption.FARM_NAME:
        return site.externalId;
      case EFarmDisplayOption.FARM_OWNER_NAME:
        return site.externalId;
      default:
        return site.externalId;
    }
  }

  if (site.siteType !== ESiteType.CLUSTER) {
    switch (siteDisplayOption) {
      case ESiteDisplayOption.SITE_ID:
        return site.title;
      case ESiteDisplayOption.SITE_NAME:
        return site.externalId;
      default:
        return site.externalId;
    }
  }

  return site.externalId;
};

export const getSiteExtraInfo = ({
  site,
  farmDisplayOption,
}: IDisplayOptionParams): string | undefined => {
  if (site.siteType === ESiteType.FARM) {
    switch (farmDisplayOption) {
      case EFarmDisplayOption.FARM_ID:
        return site.farmOwner;
      case EFarmDisplayOption.FARM_NAME:
        return site.farmOwner;
      case EFarmDisplayOption.FARM_OWNER_NAME:
        return site.title;
      default:
        return site.farmOwner;
    }
  }

  return undefined;
};
